// Client-error reporting (ED-2527).
//
// Ships throttled error reports to the PUBLIC /monitoring/client-errors
// collector on the client API, which re-emits each one through the backend log
// pipeline (CloudWatch → Better Stack) so app crashes appear in the Admin
// Console → System Health feed next to lambda errors, tagged with platform /
// screen / app version.
//
// An ErrorBoundary crash in a signed-in session also goes to the authenticated
// /monitoring/crash-report route, which posts it to the team's Slack channel
// server-side. The app never posts to Slack itself: a webhook URL in the bundle
// is a public write path into the channel. Crashes before sign-in reach only
// the collector.
//
// Two entry points:
//   reportClientError(err, { source }) — called by the ErrorBoundary and the
//     global handlers; throttled per error signature so a render loop or a
//     rejection storm can't flood the collector.
//   installGlobalErrorHandlers() — installed once at app startup (App.tsx).
//     Wraps RN's ErrorUtils global handler (native + RN-web) and, on web,
//     window 'error' + 'unhandledrejection' — the classes of failure nothing
//     caught before this existed.
//
// The endpoint is unauthenticated by design (crashes can pre-date login, and a
// broken token refresh must still be reportable); the store/navigation lookups
// are lazy + best-effort so the reporter can never itself throw or create an
// import cycle.

import { Platform } from 'react-native';

import config from '../config';
import { isElectron, isWeb } from './platform';
import { getAppVersion, getPlatformDevice } from './devicePlatform';

type ReportSource = 'boundary' | 'global' | 'promise' | 'csp';

const MAX_REPORTS_PER_SESSION = 20;
const SIGNATURE_COOLDOWN_MS = 60_000;

let sentCount = 0;
const lastSentBySignature = new Map<string, number>();
let installed = false;

type SessionContext = { cognito_id?: string; account_id?: number | string; screen?: string };

function sessionContext(): SessionContext {
  const ctx: SessionContext = {};
  try {
    const { store } = require('../store');
    const state = store.getState();
    const cid = state?.authentication?.user?.cognito_id;
    if (cid) ctx.cognito_id = cid;
    const aid = state?.session?.account?.account_id;
    if (aid != null) ctx.account_id = aid;
  } catch {
    // store not ready (module-init crash) — report without identity
  }
  try {
    const { navigationRef } = require('../navigation/navigationRef');
    if (navigationRef?.isReady?.()) ctx.screen = navigationRef.getCurrentRoute()?.name;
  } catch {
    // navigation not mounted yet
  }
  return ctx;
}

// The server takes identity from the token, so the body carries none. apiPost
// is required lazily for the same reason as the store: apiClient imports it.
function reportCrashToTeam(ctx: SessionContext, report: Record<string, string | null>): void {
  if (!ctx.cognito_id || ctx.account_id == null) return;
  try {
    const { apiPost } = require('./apiClient');
    apiPost(`/monitoring/crash-report/${ctx.account_id}/${ctx.cognito_id}`, report)
      .catch(() => { /* fire-and-forget — never surface reporting failures */ });
  } catch {
    // apiClient unavailable (module-init crash) — the collector still has it
  }
}

export function reportClientError(
  err: unknown,
  opts: { source: ReportSource; screen?: string; componentStack?: string; boundary?: string },
): void {
  try {
    const isError = err instanceof Error;
    const name = ((isError && err.name) || 'Error').slice(0, 120);
    const rawMessage = isError
      ? err.message
      : typeof err === 'string'
        ? err
        : (() => { try { return JSON.stringify(err); } catch { return String(err); } })();
    const message = String(rawMessage ?? 'Unknown error').slice(0, 600);

    // Per-signature cooldown + session cap: a crash loop reports once a minute,
    // not once a frame.
    const signature = `${name}:${message.slice(0, 160)}`;
    const now = Date.now();
    if (sentCount >= MAX_REPORTS_PER_SESSION) return;
    if (now - (lastSentBySignature.get(signature) || 0) < SIGNATURE_COOLDOWN_MS) return;
    lastSentBySignature.set(signature, now);
    sentCount += 1;

    // Mirror into the session-telemetry journey stream (post-throttle, so a
    // crash loop marks the journey once a minute, not once a frame). Lazy +
    // best-effort for the same reasons as the store/navigation lookups below.
    try {
      const { recorder } = require('../services/sessionTelemetry/recorder');
      recorder.recordError(name, message, opts.source);
    } catch { /* recorder unavailable — never block reporting */ }

    const ctx = sessionContext();
    const stack = [isError ? err.stack : null, opts.componentStack]
      .filter(Boolean)
      .join('\n')
      .slice(0, 4000);

    fetch(`${config.apiGateway.endpoint}/monitoring/client-errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        events: [{
          name,
          message,
          stack: stack || null,
          source: opts.source,
          platform: getPlatformDevice(),
          screen: opts.screen ?? ctx.screen ?? null,
          app_version: getAppVersion(),
          cognito_id: ctx.cognito_id ?? null,
          account_id: ctx.account_id ?? null,
        }],
      }),
    }).catch(() => { /* fire-and-forget — never surface reporting failures */ });

    if (opts.source === 'boundary') {
      reportCrashToTeam(ctx, {
        boundary: opts.boundary ?? 'Root',
        name,
        message,
        stack: (opts.componentStack || (isError ? err.stack : '') || '').slice(0, 2000) || null,
        platform: getPlatformDevice(),
        os_version: Platform.Version != null ? String(Platform.Version) : null,
        app_version: getAppVersion(),
        screen: opts.screen ?? ctx.screen ?? null,
      });
    }
  } catch {
    // The reporter runs inside error paths; it must never throw.
  }
}

export function installGlobalErrorHandlers(): void {
  if (installed) return;
  installed = true;

  // RN global JS handler (native + RN-web). Chain the previous handler so dev
  // redbox / default fatal behavior is preserved.
  try {
    const ErrorUtils = (global as any)?.ErrorUtils;
    if (ErrorUtils?.setGlobalHandler) {
      const prev = ErrorUtils.getGlobalHandler?.();
      ErrorUtils.setGlobalHandler((error: unknown, isFatal?: boolean) => {
        reportClientError(error, { source: 'global' });
        if (typeof prev === 'function') prev(error, isFatal);
      });
    }
  } catch {
    // ErrorUtils shape differs (tests) — skip
  }

  // Web only: uncaught window errors + unhandled promise rejections (RN has no
  // built-in hook for the latter; the boundary + global handler cover native).
  if (isWeb && typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('error', (ev: ErrorEvent) => {
      if (ev?.error) reportClientError(ev.error, { source: 'global' });
    });
    window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
      reportClientError(ev?.reason ?? 'Unhandled promise rejection', { source: 'promise' });
    });
  }

  // Desktop only: a host missing from desktop/renderer-csp.js shows up here, and
  // web's policy only reports. The host alone, because a URL can carry a token.
  if (isElectron && typeof document !== 'undefined') {
    document.addEventListener('securitypolicyviolation', (ev: SecurityPolicyViolationEvent) => {
      reportClientError(`CSP blocked ${ev.effectiveDirective}: ${blockedHost(ev.blockedURI)}`, { source: 'csp' });
    });
  }
}

function blockedHost(uri: string): string {
  try {
    return new URL(uri).host || uri.split(':')[0];
  } catch {
    return uri;
  }
}
